import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useLanguage } from '@/contexts';
import { authenticatedFetch } from '../utils/api';
import { API_URLS } from '../constants';

interface SaveNewVersionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  promptId: string;
  promptTitle: string;
  promptDescription: string;
  playgroundSystemMessage: string;
  playgroundPromptText: string;
  playgroundChangeNotes: string;
}

const SaveNewVersionModal: React.FC<SaveNewVersionModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  promptId,
  promptTitle,
  promptDescription,
  playgroundSystemMessage: _playgroundSystemMessage,
  playgroundPromptText,
  playgroundChangeNotes
}) => {
  const { t } = useLanguage();
  const [isSaving, setIsSaving] = useState(false);
  const [title, setTitle] = useState(promptTitle);
  const [description, setDescription] = useState(promptDescription);
  const [changeNotes, setChangeNotes] = useState(playgroundChangeNotes);

  // Sync internal state with props when they change
  useEffect(() => {
    setTitle(promptTitle);
    setDescription(promptDescription);
    setChangeNotes(playgroundChangeNotes);
  }, [promptTitle, promptDescription, playgroundChangeNotes]);

  const handleSave = async () => {
    if (!playgroundPromptText.trim()) {
      toast.error(t('promptNotEmpty'));
      return;
    }

    setIsSaving(true);
    try {
      const response = await authenticatedFetch(API_URLS.PROMPTS.VERSIONS.CREATE(promptId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Language': navigator.language.startsWith('es') ? 'es' : 'en',
        },
        body: JSON.stringify({
          title: title.trim() || 'Updated Prompt',
          description: description.trim() || '',
          full_text: playgroundPromptText,
          change_notes: changeNotes.trim() || t('updatedPlayground'),
          systemprompt: _playgroundSystemMessage
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        // Handle both old and new error response formats
        const errorMessage = errorData.error?.message || errorData.error || errorData.message || t('failedSaveNewVersion');
        throw new Error(errorMessage);
      }

      toast.success(t('saveNewVersionSuccess'));
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error saving new version:', error instanceof Error ? error.message : t('unknownError'));
      toast.error(error instanceof Error ? error.message : t('failedSaveNewVersion'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (!isSaving) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md relative shadow-xl">
        {/* Close Button */}
        <button
          type="button"
          onClick={handleClose}
          disabled={isSaving}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-xl font-bold focus:outline-none disabled:opacity-50"
          aria-label="Close"
        >
          ×
        </button>

        {/* Modal Title */}
        <h2 className="text-xl font-bold mb-6 text-center pr-8 text-brand-dark-blue">
          {t('saveAsNewVersion')}
        </h2>

        <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
          {/* Title Field */}
          <div className="mb-4">
            <label htmlFor="version-title" className="block text-sm font-medium text-gray-700 mb-2">
              {t('title')}
            </label>
            <input
              id="version-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-electric-blue focus:border-brand-electric-blue"
              placeholder={t('enterVersionTitle')}
              disabled={isSaving}
              required
            />
          </div>

          {/* Description Field */}
          <div className="mb-4">
            <label htmlFor="version-description" className="block text-sm font-medium text-gray-700 mb-2">
              {t('descriptionTitle')}
            </label>
            <textarea
              id="version-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-electric-blue focus:border-brand-electric-blue"
              placeholder={t('enterVersionDescription')}
              disabled={isSaving}
            />
          </div>

          {/* Change Notes Field */}
          <div className="mb-6">
            <label htmlFor="version-change-notes" className="block text-sm font-medium text-gray-700 mb-2">
              {t('changeNotes')}
            </label>
            <textarea
              id="version-change-notes"
              value={changeNotes}
              onChange={(e) => setChangeNotes(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-electric-blue focus:border-brand-electric-blue"
              placeholder={t('promptChangeNotesPlaceholder')}
              disabled={isSaving}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSaving}
              className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-electric-blue disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('cancel')}
            </button>
            <button
              type="submit"
              disabled={isSaving || !playgroundPromptText.trim()}
              className="flex-1 flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-brand-dark-blue border border-transparent rounded-md hover:bg-brand-electric-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-electric-blue disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('saving')}
                </>
              ) : (
                t('saveVersion')
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SaveNewVersionModal;
